import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchStripeData, selectStripe } from '../../store/slices/stripeSlice';
import { stripeSecureEndpoint } from '../../services/api/endpoints/stripe.secure.endpoint';
import { IrenewalCheckout } from '../../views/Settings/Billings/SubscriptionPlans';

export const useCacxiStripe = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { recurrencePlans, oneOfPlans, isLoading, error } = useSelector(selectStripe);
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const refetch = useCallback(() => {
    if (dispatch) {
      dispatch(fetchStripeData());
    }
  }, [dispatch]);

  useEffect(() => {
    if (refetch) refetch();
  }, [refetch]);

  const organizationId = selectedOrganization?.id || '';

  const getSubscriptionInfo = async (stripeCustomerId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.getSubscription(organizationId, stripeCustomerId);
      return response;
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      throw err;
    }
  };

  const getCustomerCardDetails = async (stripeCustomerId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.getCustomerCardDetails(
        organizationId,
        stripeCustomerId
      );
      return response;
    } catch (err) {
      console.error('Error fetching customer card details:', err);
      throw err;
    }
  };

  const replaceCustomerCard = async (
    stripeCustomerId: string,
    newPaymentMethodId: string,
    oldPaymentMethodId: string
  ) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.replaceCustomerCard(
        organizationId,
        stripeCustomerId,
        newPaymentMethodId,
        oldPaymentMethodId
      );
      return response;
    } catch (err) {
      console.error('Error replacing customer card:', err);
      throw err;
    }
  };

  const cancelSubscription = async (subscriptionId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.cancelSubscription(
        organizationId,
        subscriptionId
      );
      return response;
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      throw err;
    }
  };

  // renew subscription
  const renewSubscription = async (subscriptionId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.renewSubscription(organizationId, subscriptionId);
      return response;
    } catch (err) {
      console.error('Error renewing subscription:', err);
      throw err;
    }
  };

  const createSubscription = async (
    priceId: string,
    paymentMethodId: string,
    customerId?: string,
    email?: string
  ) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.createSubscription(organizationId, {
        priceId,
        customerId,
        paymentMethodId,
        email,
      });
      return response;
    } catch (err) {
      console.error('Error creating subscription:', err);
      throw err;
    }
  };

  // get customer billing history
  const getCustomerBillingHistory = async (stripeCustomerId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.getCustomerBillingHistory(
        organizationId,
        stripeCustomerId
      );
      return response;
    } catch (err) {
      console.error('Error fetching customer billing history:', err);
      throw err;
    }
  };

  const getCustomerAddonHistory = async (stripeCustomerId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.getCustomerAddonHistory(
        organizationId,
        stripeCustomerId
      );
      return response;
    } catch (err) {
      console.error('Error fetching customer addon history:', err);
      throw err;
    }
  };
  const startImmediateSubscription = async (subscriptionId: string, priceId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.startImmediateSubscription(
        organizationId,
        subscriptionId,
        priceId
      );
      return response;
    } catch (err) {
      console.error('Error starting immediate subscription:', err);
      throw err;
    }
  };

  const cancelAtPeriodEnd = async (subscriptionId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.cancelAtPeriodEnd(organizationId, subscriptionId);
      return response;
    } catch (err) {
      console.error('Error setting cancel at period end:', err);
      throw err;
    }
  };

  const createRenewalCheckoutSession = async (data: IrenewalCheckout) => {
    try {
      if (!organizationId) throw new Error('No organization selected');

      const response = await stripeSecureEndpoint.createRenewalCheckoutSession(
        data.organisationId,
        data.customerId,
        data.email,
        data.firstName,
        data.planId,
        data.isTrialEnabled,
        data.companyName
      );
      console.log('createRenewalCheckoutSession 22', response);
      return response;
    } catch (err) {
      console.error('Error creating renewal checkout session:', err);
      throw err;
    }
  };

  const buyCredits = async (priceId: string, stripeCustomerId?: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      if (!priceId) throw new Error('Price ID is required to buy credits');
      if (!stripeCustomerId) {
        throw new Error('Subscription ID is required to buy credits');
      }

      const checkoutUrl = await stripeSecureEndpoint.buyCredits(
        organizationId,
        stripeCustomerId,
        priceId
      );

      return checkoutUrl;
    } catch (err) {
      console.error('Error buying credits:', err);
      throw err;
    }
  };

  const createPaymentMethod = async (stripeCustomerId: string, paymentMethodId: string) => {
    try {
      if (!organizationId) throw new Error('No organization selected');
      const response = await stripeSecureEndpoint.createPaymentMethod(
        organizationId,
        stripeCustomerId,
        paymentMethodId
      );
      return response;
    } catch (err) {
      console.error('Error creating payment method:', err);
      throw err;
    }
  };

  return {
    recurrencePlans,
    oneOfPlans,
    isLoading,
    error,
    refetch,
    createSubscription,
    cancelSubscription,
    getSubscriptionInfo,
    getCustomerCardDetails,
    replaceCustomerCard,
    getCustomerBillingHistory,
    getCustomerAddonHistory,
    startImmediateSubscription,
    cancelAtPeriodEnd,
    renewSubscription,
    createRenewalCheckoutSession,
    buyCredits,
    createPaymentMethod,
  };
};
